"use client";

import { useState, useEffect } from "react";
import toast from "react-hot-toast";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import {
  Loader2,
} from "lucide-react";
import { formatCstDateTimeLocal } from "../utils/cstDate";

export const TournamentHomeEditModal = ({
  isOpen,
  onClose,
  transaction,
  onUpdateTransaction,
  currentUser,
  transactionDetails
}) => {
  const [formData, setFormData] = useState({
    date: "",
    notes: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [currentTransactionDetails, setCurrentTransactionDetails] = useState(transactionDetails || []);

  useEffect(() => {
    if (isOpen) {
      // Load the latest transaction details from localStorage when modal opens
      const savedDetails = localStorage.getItem('transactionDetails');
      if (savedDetails) {
        setCurrentTransactionDetails(JSON.parse(savedDetails));
      } else {
        // Fallback to prop if no localStorage data
        setCurrentTransactionDetails(transactionDetails || []);
      }
    }
  }, [isOpen, transactionDetails]);

  useEffect(() => {
    if (transaction) {
      // Parse receipts if it's a string
      let existingReceipts = [];
      if (transaction.receipts) {
        try {
          if (typeof transaction.receipts === "string") {
            // Parse the JSON string
            existingReceipts = JSON.parse(transaction.receipts);
          } else if (Array.isArray(transaction.receipts)) {
            existingReceipts = transaction.receipts;
          }
        } catch (error) {
          console.error("Error parsing receipts:", error);
          existingReceipts = [];
        }
      }

      // Ensure existingReceipts is always an array
      if (!Array.isArray(existingReceipts)) {
        existingReceipts = [];
      }

      setFormData({
        date: formatCstDateTimeLocal(transaction.date),
        notes: transaction.notes || "",
      });
    }
  }, [transaction]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  // Handle transaction detail selection
  const handleTransactionDetailSelect = (selectedDetail) => {
    if (selectedDetail && selectedDetail !== "select") {
      // Add the selected detail to the notes field
      const currentNotes = formData.notes;
      const newNotes = currentNotes 
        ? `${currentNotes}\n${selectedDetail}` 
        : selectedDetail;
      
      setFormData(prev => ({ ...prev, notes: newNotes }));
    }
  };

  const handleSubmit = async () => {
    if (!transaction) return;

    const notes = formData.notes.trim();
    const date = formData.date;

    // Validation
    if (!date) {
      setError("Please select a valid date and time.");
      return;
    }

    if (!notes) {
      setError("Please enter transaction details.");
      return;
    }

    // Proceed
    setSubmitting(true);

    const isDateUnchanged =
      formData.date === formatCstDateTimeLocal(transaction.date);

    try {
      const success = await onUpdateTransaction(transaction.id, {
        date: isDateUnchanged ? transaction.date : formData.date,
        notes,
      });

      if (success) {
        handleClose();
        toast.success("Transaction updated successfully!");
      }
    } catch (error) {
      console.error("Transaction update error:", error);
      setError("Failed to update transaction. Please try again.");
      toast.error("Failed to update transaction. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  // Reset form when modal closes
  const handleClose = () => {
    setFormData({
      date: "",
      notes: "",
    });
    setError(null);
    onClose();
  };

  if (!isOpen || !transaction) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-bold">Edit Record</h2>
          <button
            onClick={handleClose}
            className="text-gray-500 hover:text-gray-700 text-2xl"
          >
            &times;
          </button>
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-100 text-red-700 rounded-md">
            {error}
          </div>
        )}

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Date
            </label>
            <Input
              type="datetime-local"
              name="date"
              value={formData.date}
              onChange={handleChange}
              className="w-full"
            />
          </div>

          {/* Transaction Details Selection Dropdown */}
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">
              Select Transaction Detail
            </label>
            <Select onValueChange={handleTransactionDetailSelect}>
              <SelectTrigger className="w-full h-10">
                <SelectValue placeholder="Choose a transaction detail to add" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="select">Select a transaction detail...</SelectItem>
                {currentTransactionDetails && currentTransactionDetails.map((detail, index) => (
                  <SelectItem key={index} value={detail}>
                    {detail}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Transaction Details Input */}
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">
              Transaction Details *
            </label>
            <textarea
              name="notes"
              value={formData.notes}
              onChange={handleChange}
              placeholder="Enter transaction details or use the dropdown above"
              className="w-full min-h-[80px] p-3 border border-gray-300 rounded-md resize-vertical focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Updated By
            </label>
            <Input
              type="text"
              value={currentUser?.username || "Current User"}
              disabled
              className="w-full bg-gray-100"
            />
          </div>

          <div className="flex justify-end space-x-3 pt-4">
            <Button
              variant="outline"
              onClick={handleClose}
              disabled={submitting}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              className="bg-yellow-500 hover:bg-yellow-600 text-black"
              disabled={submitting}
            >
              {submitting ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  Updating...
                </>
              ) : (
                "Update"
              )}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};
